import React from 'react'
import { ScrollView } from 'react-native'

import { Icon, colors } from '@zet/design-system'
import { Stack, XStack } from '@zet/design-system/layout'
import { B2, B4, B5, B6, CTN, H5, SH3 } from '@zet/design-system/typography'

import { useApiAction } from '../../api/useApiAction'
import { brandsI18n } from '../../localization'

type KycStatusProps = {
  title: string
  subTitle: string
  applicationNumber: string
  kycSteps: Array<{ title: string; subTitle: string; icon: string }>
  insideWorkingHours?: boolean
  thingsToCarry?: string[]
}

const VideoKyc: React.FC<KycStatusProps> = ({
  title,
  subTitle,
  kycSteps,
  applicationNumber,
  insideWorkingHours,
  thingsToCarry,
}) => {
  const { data } = useApiAction()
  return (
    <Stack className="flex-1">
      <ScrollView>
        <H5 className="m-3">{title}</H5>
        <B2 className="mx-3 mb-4 text-justify">{subTitle}</B2>
        {applicationNumber && (
          <XStack className="bg-grays-20 mx-3 mb-3 items-center rounded p-2">
            <Icon
              type="DocumentOutlined"
              size={16}
              color={colors.primary[200]}
            />
            <B5 className="text-primary-200 px-2">
              {brandsI18n.t('hdfc.application')}
            </B5>
            <B4 className="text-primary-200">{applicationNumber}</B4>
          </XStack>
        )}
        <Stack className="border-primary-50 mx-3 rounded-lg  border">
          <SH3 className="text-primary-200 bg-primary-10 rounded-tl-lg rounded-tr-lg p-3">
            {brandsI18n.t('hdfc.videoKyc')}
          </SH3>
          <Stack className="p-3">
            <B4 className="text-grays-70">{brandsI18n.t('hdfc.steps')}</B4>
            {kycSteps?.map((step) => {
              return (
                <XStack className="my-2 items-center">
                  <Stack className="bg-grays-10 border-grays-30 rounded-full border p-2">
                    <Icon
                      type="CameraOutlined"
                      size={22}
                      color={colors.grays[80]}
                    />
                  </Stack>
                  <Stack className="ml-2">
                    <B4 className="text-grays-80">{step?.title}</B4>
                    <B6 className="text-grays-70">{step?.subTitle}</B6>
                  </Stack>
                </XStack>
              )
            })}
            {!!data?.metadata?.daysRemaining && (
              <XStack className="border-grays-40 mt-2 items-center border-t border-dashed p-1">
                <Icon type="Timeline" size={16} color={colors.warning[150]} />
                <CTN className="ml-2">
                  {brandsI18n.t('hdfc.daysLeft', {
                    days: data?.metadata?.daysRemaining,
                    s: data?.metadata?.daysRemaining > 1 ? 's' : '',
                  })}
                </CTN>
              </XStack>
            )}
          </Stack>
        </Stack>
        <XStack
          className={`mx-3 my-4 items-start rounded p-2 ${
            insideWorkingHours ? 'bg-primary-10' : 'bg-error-10'
          }`}
        >
          <Icon
            type={insideWorkingHours ? 'ClockOutlined' : 'ErrorOutlined'}
            size={22}
            color={
              insideWorkingHours ? colors.primary[150] : colors.warning[150]
            }
            className="mt-1"
          />
          <B5 className="ml-2">
            {brandsI18n.t(
              insideWorkingHours
                ? 'hdfc.insideWorkingHours'
                : 'hdfc.outsideWorkingHours'
            )}
          </B5>
        </XStack>
        <Stack className="border-grays-40 mx-3 border-t border-dashed py-2">
          <SH3 className="mb-2">{brandsI18n.t('hdfc.thingsToCarry')}</SH3>
          {thingsToCarry?.map((thingsToCarry) => {
            return (
              <XStack className="mb-1 items-center">
                <Icon type="DoneFilled" size={16} color={colors.success[150]} />
                <B5 className="text-grays-70 ml-2">{thingsToCarry}</B5>
              </XStack>
            )
          })}
        </Stack>
      </ScrollView>
    </Stack>
  )
}

export default VideoKyc
